import React, {
  ChangeEvent,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "components/Button/Button";
import {
  Content,
  ContentWrapper,
  DrawerFooter,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ActionMeta } from "react-select";
import { createStructuredSelector } from "reselect";
import Select from "../../../../components/Select/Select";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import {
  getInboundAddresses,
  getInboundCsvExample,
  importInboundShipmentsCsv,
} from "../../../../reduxFolder/reducers/inbound";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import {
  inboundSelectAddresses,
  inboundSelectAddressesLoaded,
  inboundSelectAddressesLoading,
} from "../../../../reduxFolder/selectors/inboundSelectors";
import { FieldWrapper } from "../../../../styles/formStyles";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import { downloadCsvFile } from "../../../../utils/downloadCsvFile";
import {
  toastResponseError,
  toastResponseSuccess,
} from "../../../../utils/responseMessageHelper";
import { toastInboundImportErrors } from "../../helpers/toastInboundImportErrors";
import {
  buildInboundAddressOptions,
  InboundAddressOption,
} from "../InboundShipmentPanel/inboundShipmentFormHelper";
import {
  Description,
  ExampleButtonWrapper,
  FileName,
  FileRow,
} from "./InboundImportPanel.styles";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const CSV_EXAMPLE_FILENAME = "inbound_shipment_packages_example.csv";

interface InboundImportPanelProps {
  addresses: any;
  isAddressesLoading: boolean;
  addressesLoaded: boolean;
  getInboundAddresses: () => Promise<any>;
  getInboundCsvExample: () => Promise<any>;
  importInboundShipmentsCsv: (data: {
    file: File;
    inboundAddressId: number;
  }) => Promise<{ value: Map<string, any> }>;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
}

const InboundImportPanel: FC<InboundImportPanelProps> = React.memo(
  ({
    addresses,
    isAddressesLoading,
    addressesLoaded,
    getInboundAddresses,
    getInboundCsvExample,
    importInboundShipmentsCsv,
    openRightPanel,
    closeRightPanel,
  }) => {
    const { t } = useTranslation("common");
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [address, setAddress] = useState<InboundAddressOption | null>(null);
    const [isImporting, setIsImporting] = useState(false);

    useFetchOnceEffect(!addressesLoaded, () =>
      getInboundAddresses().catch(toastResponseError),
    );

    const addressOptions = useMemo(
      () => buildInboundAddressOptions(addresses),
      [addresses],
    );

    const handleChangeSelect = useCallback(
      (selectedOption: unknown, _context: ActionMeta<unknown>) => {
        setAddress(selectedOption as InboundAddressOption | null);
      },
      [],
    );

    const handleFileChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files ? event.target.files[0] : null;
        // reset synchronously so re-selecting the same fixed file re-fires onChange
        event.target.value = "";
        if (!selected) return;

        if (!selected.name.toLowerCase().endsWith(".csv")) {
          toastResponseError(t("inbound.import.wrongFormat"));
          return;
        }
        if (selected.size > MAX_FILE_SIZE_BYTES) {
          toastResponseError(t("inbound.import.tooLarge"));
          return;
        }

        setFile(selected);
      },
      [t],
    );

    // The example endpoint sits behind the token-auth headers, so it has to
    // go through the api client and become a client-side download.
    const handleDownloadExample = useCallback(() => {
      getInboundCsvExample()
        .then((response: any) => {
          const csv = response?.value?.get("data");
          if (csv) downloadCsvFile(csv, CSV_EXAMPLE_FILENAME);
        })
        .catch(toastResponseError);
    }, [getInboundCsvExample]);

    const handleImport = useCallback(() => {
      if (!file || !address) return;

      setIsImporting(true);
      importInboundShipmentsCsv({ file, inboundAddressId: address.value })
        .then(({ value }) => {
          toastResponseSuccess(t("inbound.import.success"));
          // The reducer already unshifted the row; open the created shipment
          // the same way the create flow does.
          openRightPanel(
            "INBOUND_SHIPMENT",
            Map({ id: value.getIn(["data", "id"]) }),
          );
        })
        .catch((error) => toastInboundImportErrors(error, t))
        .finally(() => setIsImporting(false));
    }, [file, address, importInboundShipmentsCsv, openRightPanel, t]);

    return (
      <ContentWrapper>
        <Heading>{t("inbound.import.title")}</Heading>
        <Content>
          <div>
            <Description>{t("inbound.import.description")}</Description>
            <FieldWrapper>
              <Select
                placeholder={t("inbound.form.address")}
                name="address"
                value={address}
                onChange={handleChangeSelect}
                options={addressOptions}
                isLoading={isAddressesLoading}
                disabled={isImporting}
              />
            </FieldWrapper>
            <ExampleButtonWrapper>
              <Button
                type={ButtonType.Button}
                color={ButtonColor.Primary}
                onClick={handleDownloadExample}
              >
                {t("inbound.import.downloadExample")}
              </Button>
            </ExampleButtonWrapper>
            <FileRow>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFileChange}
              />
              <Button
                variant={ButtonVariant.Bordered}
                color={ButtonColor.Neutral}
                type={ButtonType.Button}
                disabled={isImporting}
                onClick={() => inputRef.current?.click()}
              >
                {t("inbound.import.selectFile")}
              </Button>
              {file && <FileName>{file.name}</FileName>}
            </FileRow>
            <DrawerFooter>
              <Button disabled={isImporting} onClick={closeRightPanel}>
                {t("common.cancel")}
              </Button>
              <Button
                color={ButtonColor.Primary}
                variant={ButtonVariant.Filled}
                isLoading={isImporting}
                disabled={isImporting || !file || !address}
                onClick={handleImport}
              >
                {t("inbound.import.submit")}
              </Button>
            </DrawerFooter>
          </div>
        </Content>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    addresses: inboundSelectAddresses,
    isAddressesLoading: inboundSelectAddressesLoading,
    addressesLoaded: inboundSelectAddressesLoaded,
  }),
  {
    getInboundAddresses,
    getInboundCsvExample,
    importInboundShipmentsCsv,
    openRightPanel,
    closeRightPanel,
  },
);

export default withConnect(InboundImportPanel);
